import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Lock } from 'lucide-react';
import UserManagement from '@/components/UserManagement';
import PipelineManagement from '@/components/pipeline/PipelineManagement';
import SimpleContactsManagement from '@/components/contacts/SimpleContactsManagement';
import { AdminClosingCostManager } from '@/components/closing-costs/AdminClosingCostManager';
import { PreApprovalDashboard } from '@/components/pre-approval/PreApprovalDashboard';
import { TemplateBuilder } from '@/components/pre-approval/TemplateBuilder';
import { VisualPreApprovalWizard } from '@/components/pre-approval/VisualPreApprovalWizard';
import { MENU_FEATURE_FLAGS, menuSections } from '@/config/menu-config';
import { getSectionPermissions } from '@/config/section-access';
import type { PreApprovalData } from '@/types/pre-approval';
import { CompanyProfileForm } from '@/components/CompanyProfileForm';
import { UserFormEnhanced } from '@/components/users/LoanOfficerProfileFormEnhanced';
import { UnifiedUserProfile } from '@/components/users/UnifiedUserProfile';
import { BranchOfficeManagement } from '@/components/profiles/BranchOfficeManagement';
import { CommunicationsDashboard } from '@/components/communications/CommunicationsDashboard';
import { InvestorsPageGate } from '@/components/investors/InvestorsPageGate';
import { RolePermissionsMatrix } from '@/components/admin/RolePermissionsMatrix';
import { cn } from '@/lib/utils';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AISupport } from '@/components/ai-support';
import AIAdminDashboard from '@/components/ai-admin/AIAdminDashboard';
import { ProspectDashboard } from '@/components/encompass/prospects/ProspectDashboard';
import { LoanDataExportsPage } from '@/components/encompass/data-exports';
import FieldDiscovery from '@/components/encompass/FieldDiscovery';
import { Recruiting } from '@/components/recruiting/Recruiting';
import { LoProspectsPage } from '@/components/lo-prospects/LoProspectsPage';
import { BrokerRecon } from '@/components/broker-recon/BrokerRecon';
import { BankerRecon } from '@/components/banker-recon/BankerRecon';
import { ServicingDashboard } from '@/components/servicing/ServicingDashboard';
import { NotificationManagement } from '@/components/admin/notification-management';
import { MyEmailNotifications } from '@/components/my-email-notifications';
import { OBBuilder } from '@/components/admin/ob-builder';
import { ConditionTemplateAdmin } from '@/components/admin/ConditionTemplateAdmin';
import { UserCompensation } from '@/components/settings/UserCompensation';
import { GoogleBusinessProfileSettings } from '@/components/settings/GoogleBusinessProfileSettings';
import { WiseboardDashboard } from '@/components/wiseboard/WiseboardDashboard';
import { FeedbackPage } from '@/components/feedback/FeedbackPage';
import { IncomeCalculationHub } from '@/components/income-calculation/IncomeCalculationHub';
import { DisclosureDashboard } from '@/components/dashboard';
import { TitleEscrowPage } from '@/components/title-escrow/TitleEscrowPage';
import { CompanyReportsHub, PipelinePerformanceReport, PipelineStatusReport, FundedLoanReport, LOScoreboardReport, PickupReport, CompanyRevenueReport, MyCommissionsReport, FraudAlertsReport, AppraisalDeliveryReport } from '@/components/reports';
import { PickupWorkspace, type PickupWorkspaceTab } from '@/components/secondary/PickupWorkspace';
import { LwThemeScope } from '@/components/lw';
import { HomeDashboard } from '@/components/home/HomeDashboard';
import { CompanySettingsHub } from '@/components/settings/CompanySettingsHub';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

interface RoleAccess {
  currentRole: {
    name: string;
    color: string;
  };
  hasAnyPermission: (permissions: string[]) => boolean;
  isLoadingPermissions?: boolean;
  userPermissions?: string[];
  getCurrentUser?: () => { roles?: string[] } | null;
}

interface MainContentProps {
  activeItem: string;
  roleAccess: RoleAccess;
  setActiveItem: (item: string) => void;
  onOpenArtemis?: () => void;
  isCustomizing?: boolean;
  onCustomizingChange?: (isCustomizing: boolean) => void;
  isEffectiveAdmin?: boolean;
  loanOfficerHasAnyPermission?: (permissions: string[]) => boolean;
  isLoadingLoanOfficerPermissions?: boolean;
  canCustomizeHome?: boolean;
}

export const MainContent: React.FC<MainContentProps> = ({
  activeItem,
  roleAccess,
  setActiveItem,
  onOpenArtemis,
  isCustomizing = false,
  onCustomizingChange = () => undefined,
  isEffectiveAdmin = false,
  loanOfficerHasAnyPermission = () => false,
  isLoadingLoanOfficerPermissions = false,
  canCustomizeHome = true
}) => {
  const [_searchParams] = useSearchParams();
  const navigate = useNavigate();
  const pickupRepriceOpportunitiesFlag = useFeatureFlag(
    MENU_FEATURE_FLAGS.pickupRepriceOpportunities
  );
  const myEmailNotificationsFlag = useFeatureFlag(
    MENU_FEATURE_FLAGS.myEmailNotifications
  );
  const pickupCurrentRoles = roleAccess.getCurrentUser?.()?.roles ?? [];
  const pickupHasResolvedRoleSnapshot = typeof roleAccess.getCurrentUser === 'function';
  const pickupIsPipelineAdmin = pickupHasResolvedRoleSnapshot
    ? pickupCurrentRoles.some((role) => role.toLowerCase() === 'admin')
    : roleAccess.hasAnyPermission(['Admin']);
  const pickupCanRunPricingActions = pickupIsPipelineAdmin
    || (roleAccess.userPermissions
      ? roleAccess.userPermissions.includes('generate_reports')
      : roleAccess.hasAnyPermission(['generate_reports']));
  // State for pre-approval pre-filled data
  const [preApprovalPrefilledData, setPreApprovalPrefilledData] = React.useState<PreApprovalData | null>(null);

  const isPickupFeatureSection = activeItem === 'pickup-report'
    || activeItem === 'pickup-pipeline';
  if (isPickupFeatureSection && pickupRepriceOpportunitiesFlag.loading) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading Pick Up access...
      </div>
    );
  }
  // Route-level gate for My Email Notifications: the sidebar hides the entry, but a typed
  // `?section=` URL still lands here, so the flag is checked again before rendering.
  if (activeItem === 'my-email-notifications' && myEmailNotificationsFlag.loading) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading My Email Notifications...
      </div>
    );
  }

  const sectionPermissions = getSectionPermissions(activeItem, {
    pickupRepriceOpportunitiesEnabled: pickupRepriceOpportunitiesFlag.enabled
  });
  if (sectionPermissions.length > 0) {
    if (roleAccess.isLoadingPermissions) {
      return (
        <div className="flex h-full items-center justify-center text-muted-foreground">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Loading permissions...
        </div>
      );
    }

    if (!roleAccess.hasAnyPermission(sectionPermissions)) {
      return (
        <div className="p-6">
          <Card className="mx-auto max-w-xl">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Access denied</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              You do not have permission to open this section.
            </CardContent>
          </Card>
        </div>
      );
    }
  }

  if (activeItem === 'my-email-notifications' && !myEmailNotificationsFlag.enabled) {
    return (
      <div className="p-6">
        <Card className="mx-auto max-w-xl">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-muted-foreground" />
              <CardTitle>My Email Notifications unavailable</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            This page is not enabled for your account.
          </CardContent>
        </Card>
      </div>
    );
  }

  if (activeItem === 'pickup-pipeline' && !pickupRepriceOpportunitiesFlag.enabled) {
    return (
      <div className="p-6">
        <Card className="mx-auto max-w-xl">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Pick Up Pipeline unavailable</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            This workspace is not enabled for your organization.
          </CardContent>
        </Card>
      </div>
    );
  }

  const renderMainContent = () => {
    switch (activeItem) {
      case 'home':
        return (
          <HomeDashboard
            currentRole={roleAccess.currentRole}
            roleAccess={roleAccess}
            onSetActiveItem={setActiveItem}
            launchers={onOpenArtemis ? { artemis: onOpenArtemis } : undefined}
            isCustomizing={isCustomizing}
            onCustomizingChange={onCustomizingChange}
            isEffectiveAdmin={isEffectiveAdmin}
            loanOfficerHasAnyPermission={loanOfficerHasAnyPermission}
            isLoadingLoanOfficerPermissions={isLoadingLoanOfficerPermissions}
            canCustomizeHome={canCustomizeHome}
          />
        );

      case 'company-settings':
        return (
          <CompanySettingsHub
            hasAnyPermission={roleAccess.hasAnyPermission}
            onNavigate={setActiveItem}
          />
        );

      case 'disclosures':
        return <DisclosureDashboard />;

      case 'manage-users':
        return <UserManagement />;
      
      case 'edit-user':
        // Get userId from search params
        return <UnifiedUserProfile />;
      
      case 'closing-cost-admin':
        return <AdminClosingCostManager />;

      case 'title-escrow-companies':
        return <TitleEscrowPage />;

      case 'condition-templates':
        return <ConditionTemplateAdmin />;

      case 'company-profile':
        return <CompanyProfileForm />;
      
      
      case 'branch-offices':
        return <BranchOfficeManagement />;

      case 'manage-investors':
      case 'view-investors':
        return <InvestorsPageGate />;

      case 'messages':
        return <CommunicationsDashboard />;

      case 'ai-support':
        return <AISupport />;

      case 'ai-settings':
        // Permission guard - only Admin role can access AI Admin
        if (!roleAccess.hasAnyPermission(['Admin'])) {
          return (
            <div className="flex min-h-screen items-center justify-center bg-background">
              <div className="text-center p-6">
                <div className="mb-4 text-muted-foreground">
                  <svg className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h2 className="mb-2 text-xl font-semibold text-foreground">Access denied</h2>
                <p className="text-muted-foreground">You do not have permission to access AI Admin.</p>
                <p className="mt-2 text-sm text-muted-foreground">Admin role required. Contact your administrator.</p>
              </div>
            </div>
          );
        }
        return <AIAdminDashboard />;

      case 'role-permissions':
        return <RolePermissionsMatrix />;

      case 'loan-center':
      case 'prospect-loans':
        return (
          <ProspectDashboard
            canGenerateReports={roleAccess.hasAnyPermission(['generate_reports'])}
            onOpenDataExports={() => setActiveItem('loan-data-exports')}
            onViewDocuments={(loan) => {
              // Navigate to URL-based document viewer (persists on refresh)
              navigate(`/internal/documents/${loan.encompass_loan_id}`);
            }}
          />
        );

      case 'loan-data-exports':
        return <LoanDataExportsPage />;

      case 'income-calculation':
        return <IncomeCalculationHub />;

      case 'field-discovery':
        return <FieldDiscovery />;

      case 'email-notifications':
        return <NotificationManagement />;
      case 'my-email-notifications':
        return <MyEmailNotifications />;
      case 'ob-builder':
        return <OBBuilder />;

      case 'system-status':
        return <WiseboardDashboard />;

      case 'recruiting':
        return <Recruiting />;

      case 'lo-prospects':
        return <LoProspectsPage />;

      case 'pipeline':
      case 'active-loans':
      case 'closed-loans':
        return <PipelineManagement />;
      
      case 'borrowers':
        return (
          <SimpleContactsManagement
            viewMode="borrowers"
            onCreatePreApproval={(prefilledData) => {
              setPreApprovalPrefilledData(prefilledData);
              setActiveItem('pre-approval-create');
            }}
          />
        );
      
      case 'referral-partners':
        return <SimpleContactsManagement viewMode="partners" />;
      
      case 'pre-approval-templates':
        return <TemplateBuilder />;
      
      case 'my-profile':
        return (
          <LwThemeScope className="lw-ds-page lw-user-profile">
            <div className="wrap">
              <nav className="lw-user-profile__breadcrumb" aria-label="Breadcrumb">
                <span>Company Settings</span>
                <span aria-hidden="true">›</span>
                <span className="lw-user-profile__breadcrumb-current" aria-current="page">My Profile</span>
              </nav>
              <UserFormEnhanced
                isAdminView={false}
                managementMode="self"
                showCompensation={false}
                showSelfCompensation={true}
                showBranchSelection={false}
              />
            </div>
          </LwThemeScope>
        );
      
      case 'broker-recon':
        return <BrokerRecon />;

      case 'banker-recon':
        return <BankerRecon />;

      case 'my-commissions':
        return <MyCommissionsReport />;

      case 'servicing':
        return (
          <ServicingDashboard
            mode={roleAccess.hasAnyPermission(['manage_compensation_plans']) ? 'accounting' : 'loan-officer'}
          />
        );

      case 'user-compensation':
        return <UserCompensation />;

      case 'google-business-profile':
        return <GoogleBusinessProfileSettings />;

      case 'pre-approval':
        return (
          <PreApprovalDashboard 
            onCreatePreApproval={(prefilledData) => {
              // Store pre-filled data and show the pre-approval creation page
              setPreApprovalPrefilledData(prefilledData);
              setActiveItem('pre-approval-create');
            }}
          />
        );
      
      case 'pre-approval-create':
        return (
          <VisualPreApprovalWizard
            prefilledData={preApprovalPrefilledData}
            onBack={() => {
              setPreApprovalPrefilledData(null);
              setActiveItem('pre-approval');
            }}
            onComplete={() => {
              setPreApprovalPrefilledData(null);
              setActiveItem('pre-approval');
            }}
          />
        );

      case 'feedback':
        return <FeedbackPage />;

      case 'company-reports':
        return (
          <CompanyReportsHub
            onNavigate={setActiveItem}
            showLegacyPickup={
              !pickupRepriceOpportunitiesFlag.loading
              && !pickupRepriceOpportunitiesFlag.enabled
            }
            hasAnyPermission={roleAccess.hasAnyPermission}
          />
        );

      case 'pipeline-performance-report':
        return <PipelinePerformanceReport />;

      case 'pipeline-status-report':
        return <PipelineStatusReport />;

      case 'funded-loan-report':
        return <FundedLoanReport />;

      case 'lo-scoreboard-report':
        return <LOScoreboardReport />;

      case 'pickup-report':
        return pickupRepriceOpportunitiesFlag.enabled
          ? (
            <PickupWorkspace
              activeTab="analysis"
              canRunPricingActions={pickupCanRunPricingActions}
              canManagePipelineActions={pickupIsPipelineAdmin}
              onTabChange={(tab: PickupWorkspaceTab) => {
                setActiveItem(tab === 'analysis' ? 'pickup-report' : 'pickup-pipeline');
              }}
            />
          )
          : <PickupReport />;

      case 'pickup-pipeline':
        return (
          <PickupWorkspace
            activeTab="pipeline"
            canRunPricingActions={pickupCanRunPricingActions}
            canManagePipelineActions={pickupIsPipelineAdmin}
            onTabChange={(tab: PickupWorkspaceTab) => {
              setActiveItem(tab === 'analysis' ? 'pickup-report' : 'pickup-pipeline');
            }}
          />
        );

      case 'company-revenue-report':
        return <CompanyRevenueReport />;

      case 'fraud-alerts-report':
        return <FraudAlertsReport />;
      case 'appraisal-delivery-report':
        return <AppraisalDeliveryReport />;

      default:
        return (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-foreground mb-2">
                {menuSections.flatMap(s => s.items).find(item => item.id === activeItem)?.title || 'Page'}
              </h1>
              <p className="text-muted-foreground">
                This section is under development. Content will be implemented here.
              </p>
            </div>
          </div>
        );
    }
  };

  // Get the current page title
  const getCurrentPageTitle = () => {
    // Special case for edit-user
    if (activeItem === 'edit-user') {
      return 'Edit User';
    }
    const allItems = menuSections.flatMap(s => s.items);
    const currentItem = allItems.find(item => item.id === activeItem);
    return currentItem?.title || 'Dashboard';
  };

  return (
    <div className="flex h-full w-full flex-1 flex-col overflow-x-hidden">
      {/* Page Header - Skip for routes that have their own header */}
      {activeItem !== 'home' && activeItem !== 'company-settings' && activeItem !== 'borrowers' && activeItem !== 'referral-partners' && activeItem !== 'pre-approval' && activeItem !== 'recruiting' && activeItem !== 'lo-prospects' && activeItem !== 'system-status' && activeItem !== 'feedback' && activeItem !== 'user-compensation' && activeItem !== 'google-business-profile' && activeItem !== 'income-calculation' && activeItem !== 'manage-investors' && activeItem !== 'manage-users' && activeItem !== 'edit-user' && activeItem !== 'my-profile' && activeItem !== 'view-investors' && activeItem !== 'banker-recon' && activeItem !== 'disclosures' && activeItem !== 'email-notifications' && activeItem !== 'my-commissions' && activeItem !== 'loan-data-exports' && activeItem !== 'fraud-alerts-report' && activeItem !== 'appraisal-delivery-report' && activeItem !== 'pickup-pipeline' && !(activeItem === 'pickup-report' && pickupRepriceOpportunitiesFlag.enabled) && (
        <div className="shrink-0 border-b bg-card/80 px-4 py-4 backdrop-blur-sm sm:px-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-warning">Lendwise operations</p>
            <h1 className="font-serif text-2xl font-medium text-primary">{getCurrentPageTitle()}</h1>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={cn(
        // Most routes scroll at the page level. These workspaces own independent pane scrolling internally.
        activeItem === 'email-notifications' || activeItem === 'pickup-pipeline'
          ? "overflow-hidden"
          : "overflow-y-auto overscroll-contain",
        "min-h-0 flex-1",
        activeItem !== 'home' && activeItem !== 'company-settings' && activeItem !== 'income-calculation' && activeItem !== 'manage-investors' && activeItem !== 'manage-users' && activeItem !== 'edit-user' && activeItem !== 'my-profile' && activeItem !== 'view-investors' && activeItem !== 'banker-recon' && activeItem !== 'disclosures' && activeItem !== 'email-notifications' && activeItem !== 'lo-prospects' && activeItem !== 'recruiting' && activeItem !== 'fraud-alerts-report' && activeItem !== 'appraisal-delivery-report' && activeItem !== 'pickup-pipeline' && !(activeItem === 'pickup-report' && pickupRepriceOpportunitiesFlag.enabled) && "p-4 sm:p-6"
      )}>
        {renderMainContent()}
      </div>
    </div>
  );
};
